import { createContext, useContext } from 'react';

import { NavigableItem, NavigationService } from 'services/schemableFormNavigation/schemableFormNavigation';
import { GenericError } from 'support/helpers/errors/errors';

import { useNavigableItemPopover } from './useNavigableItemPopover';

export type NavigableAnnotationPopoverProps<T extends NavigableItem> = {
  style?: React.CSSProperties;
  annotation: T | undefined;
  viewMode: boolean;
  fieldDescription?: React.ReactNode;
  isValidating?: boolean;
  isPendingValidation?: boolean;
  ref?: React.Ref<HTMLDivElement>;
};

export type SchemableFormNavigationContextType<T extends NavigableItem> = {
  containerRef?: React.RefObject<HTMLElement | null>;
  NavigableItemPopoverComponent: React.ComponentType<NavigableAnnotationPopoverProps<T>> | null;
  service: Omit<NavigationService<T>, 'setItems'>;
  useNavigableItemPopover: typeof useNavigableItemPopover;
};

export const SchemableFormNavigationContext = createContext<SchemableFormNavigationContextType<NavigableItem> | null>(
  null,
);

export const useNavigationContext = <T extends NavigableItem>(): SchemableFormNavigationContextType<T> => {
  const context = useContext(SchemableFormNavigationContext);
  if (!context) {
    throw new GenericError('useNavigationContext must be used within a SchemableFormNavigationContextProvider');
  }
  // Downcast to an unresolved type parameter: irreducible. Even with a fully
  // covariant context type, TS cannot narrow NavigableItem to T (TS2352 —
  // "T could be instantiated with a different subtype"). The provider guarantees
  // the two agree; that check lives on the provider's props.
  return context as unknown as SchemableFormNavigationContextType<T>;
};
